/**
 * Stage 1: Origin (#3540).
 *
 * One question, then the starting realms as index entries (each the capital
 * of its realm, prose verbatim from the StartingArea row). Reading is free;
 * the realm enters when the player chooses (never on hover, Decision 6), and
 * choosing a different realm asks first when it would clear a stage that
 * depended on it (#4022: no dependent choice, no question). The record rail lists the choice; it says nothing else
 * (Decision 8). The first name is asked here too (#4874, ruled 2026-10-19): the rail and
 * the life then read "Marisol was born" instead of "you"; it saves on blur by itself.
 */

import { useRealmTheme } from '@/components/realm-theme-provider';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChapterLeaf,
  ConfirmDialog,
  Entry,
  EntryDoors,
  EntryList,
  Field,
  Marginalia,
  Note,
  PageTurn,
  Paragraphs,
  RecordRail,
  stageEyebrow,
} from '../folio';
import { useCGExplanations, useStartingAreas, useUpdateDraft } from '../queries';
import { Stage, STAGE_LABELS } from '../types';
import type { CharacterDraft, StartingArea } from '../types';
import { getRealmTheme } from '../utils';

interface OriginStageProps {
  draft: CharacterDraft;
  onStageSelect: (stage: Stage) => void;
}

export function OriginStage({ draft, onStageSelect }: OriginStageProps) {
  const { data: areas, isLoading, error } = useStartingAreas();
  const { data: copy } = useCGExplanations();
  const updateDraft = useUpdateDraft();
  const { setRealmTheme } = useRealmTheme();
  const [pending, setPending] = useState<StartingArea | null>(null);

  const chosen = draft.selected_area;
  const firstName = draft.draft_data.first_name ?? '';
  const saveFirstName = (value: string) => {
    if (value.trim() === firstName) return;
    updateDraft.mutate({ draftId: draft.id, data: { draft_data: { first_name: value.trim() } } });
  };

  const apply = (area: StartingArea | null) => {
    if (area) setRealmTheme(getRealmTheme(area));
    const changing = chosen?.id !== area?.id;
    updateDraft.mutate({
      draftId: draft.id,
      data: {
        selected_area_id: area?.id ?? null,
        ...(changing && {
          selected_beginnings_id: null,
          selected_species_id: null,
          family_id: null,
        }),
      },
    });
  };

  // Beginnings, species and family depend on the realm; the confirm exists to
  // protect those, so it appears only when one of them is set (#4022).
  const hasDependents = Boolean(
    draft.selected_beginnings || draft.selected_species || draft.family
  );

  const choose = (area: StartingArea) => {
    if (chosen && chosen.id !== area.id && hasDependents) {
      setPending(area);
      return;
    }
    apply(area);
  };

  if (isLoading)
    return (
      <p className="ledger-line" aria-busy="true">
        Loading starting realms…
      </p>
    );
  if (error)
    return <p className="ledger-line">The starting realms could not be read. Try again.</p>;

  return (
    <>
      <ChapterLeaf
        stage={Stage.ORIGIN}
        title={copy?.origin_heading ?? 'Where does the story begin?'}
        aside={
          <>
            <RecordRail
              rows={[
                // Absent (not "not yet chosen") until a first name exists (#3630).
                ...(firstName ? [{ label: 'Name', value: firstName }] : []),
                { label: 'Origin', value: chosen?.name },
              ]}
              ledger={stageEyebrow(draft.current_stage)}
            />
            <Marginalia id="note-change">
              <Note lead="Changing your starting realm">
                clears the stages that depended on it. You will be asked first.
              </Note>
            </Marginalia>
          </>
        }
      >
        <Field
          id="first_name"
          label={copy?.origin_first_name_label ?? 'First name'}
          hint={copy?.origin_first_name_hint ?? '2-20 characters, letters only.'}
        >
          <input
            id="first_name"
            type="text"
            maxLength={20}
            defaultValue={firstName}
            onBlur={(e) => saveFirstName(e.target.value)}
          />
        </Field>
        <EntryList label="Starting realms">
          {areas?.map((area) => {
            const isChosen = chosen?.id === area.id;
            // The realm's formal name as staff wrote it; its plain name when they wrote none.
            const realmName = area.realm_formal_name || area.realm_name || '';
            return (
              <Entry
                key={area.id}
                name={area.name}
                tag={realmName}
                chosen={isChosen}
                open={isChosen}
                onChoose={() => choose(area)}
                onSetAside={() => apply(null)}
              >
                <Paragraphs text={area.description} />
                {area.realm_slug && (
                  <p className="ledger-line">
                    <Link to={`/realms/${area.realm_slug}`}>
                      About {area.realm_name ?? realmName} <span aria-hidden="true">→</span>
                    </Link>
                  </p>
                )}
                <EntryDoors
                  chooseLabel={`Select ${area.name}`}
                  onChoose={() => choose(area)}
                  chosen={isChosen}
                  onSetAside={() => apply(null)}
                />
              </Entry>
            );
          })}
        </EntryList>
        <PageTurn
          next={{
            label: `Next: ${STAGE_LABELS[Stage.HERITAGE]}`,
            onClick: () => onStageSelect(Stage.HERITAGE),
            disabled: !chosen,
            reason: 'Choose a starting realm to continue.',
          }}
        />
      </ChapterLeaf>
      <ConfirmDialog
        open={pending !== null}
        title="Change starting realm"
        confirmLabel="Change realm"
        cancelLabel="Keep current choice"
        onConfirm={() => {
          if (pending) apply(pending);
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      >
        Changing your starting realm clears the stages that depended on it (Heritage, Lineage and
        Species choices).
      </ConfirmDialog>
    </>
  );
}
